// @ts-check
// Cartão de um passo no canvas (spec 2.0.0, fluxos.md seção 20.5, "Nó"; contrato 09 de fluxos, seção 17): círculo com a
// cor da categoria, nome da ação (ou do passo) e o resumo, o selo de pendência (âmbar para aviso, vermelho para erro),
// o botão de excluir quando selecionado e, com "Números" ligado, as pílulas de quem está ali agora, passou, falhou e
// saiu, cada uma abrindo a lista dos cards.
import { html } from 'htm/preact';
import { Icon } from '../../ui/icon.js';
import { FlowCategoryCircle, FlowCountLink, FlowSummaryParts, flowProblemText } from './flow-ui.js';
import { t } from '../../i18n/index.js';
import { flowNodePassedCount } from '../../lib/flow-drilldown.js';

/**
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowNode} FlowNode
 * @typedef {import('../../../../shared/flows/flow-summary.js').FlowNodeSummary} FlowNodeSummary
 * @typedef {import('../../../../shared/flows/flow-problems.js').FlowProblem} FlowProblem
 * @typedef {import('../../data/flows.js').FlowNodeCount} FlowNodeCount
 */

/**
 * Texto da pendência do passo (o primeiro erro, ou o primeiro aviso).
 * @param {FlowProblem[]} problems @param {((problem: any) => string) | null} [textOf] o texto das pendências de outra área (os bots)
 */
export function nodeProblemText(problems, textOf = null) {
  const first = problems.find((problem) => problem.severity === 'ERROR') ?? problems[0];
  return first ? (textOf ?? flowProblemText)(first) : '';
}

/**
 * @param {{ node: FlowNode, summary: FlowNodeSummary, title: string, summaryText: string, problems: FlowProblem[], stepNumber: number, level: number,
 *   isSelected: boolean, isFocused: boolean, isReadOnly?: boolean, count?: FlowNodeCount | null, showCounts?: boolean, flowId?: string | null,
 *   period?: { from?: string | null, to?: string | null }, onSelect?: (nodeId: string) => void, onDelete?: (nodeId: string) => void,
 *   circle?: any, problemTextOf?: ((problem: any) => string) | null, countsContent?: any }} props
 *   circle, problemTextOf e countsContent: o círculo, o texto da pendência e os números de outra área (os bots); sem eles, os dos fluxos
 */
export function FlowNodeCard({
  node, summary, title, summaryText, problems, stepNumber, level, isSelected, isFocused, isReadOnly = false, count = null, showCounts = false, flowId = null,
  period = {}, onSelect, onDelete, circle = undefined, problemTextOf = null, countsContent = undefined,
}) {
  const hasError = problems.some((problem) => problem.severity === 'ERROR');
  const hasWarning = !hasError && problems.length > 0;
  const problemText = problems.length > 0 ? nodeProblemText(problems, problemTextOf) : '';
  const nowCount = count?.now ?? 0;
  const countsText = showCounts && count ? t('flows.counts.now', { count: nowCount }) : '';
  const ariaLabel = t('flows.canvas.nodeLabel', {
    number: stepNumber, title, summary: summaryText, counts: [countsText, problemText].filter(Boolean).join(' '),
  }).trim();
  const problemId = `flow-node-problem-${node.id}`;
  return html`<div class="flow-node" data-node-id=${node.id}>
    <div
      class="flow-node-card"
      role="treeitem"
      aria-level=${level}
      aria-selected=${isSelected ? 'true' : 'false'}
      aria-label=${ariaLabel}
      aria-describedby=${problemText ? problemId : undefined}
      tabindex=${isFocused ? 0 : -1}
      data-focus-id=${node.id}
      data-selected=${isSelected ? 'true' : undefined}
      data-problem=${hasError ? 'error' : hasWarning ? 'warning' : undefined}
      data-unconfigured=${summary.isUnconfigured ? 'true' : undefined}
      data-readonly=${isReadOnly ? 'true' : undefined}
      onClick=${(/** @type {MouseEvent} */ event) => {
        event.stopPropagation();
        onSelect?.(node.id);
      }}
    >
      ${circle ?? html`<${FlowCategoryCircle} type=${node.type} config=${node.config} />`}
      <span class="flow-node-texts">
        <span class="flow-node-title">${title}</span>
        <span class="flow-node-summary" data-unconfigured=${summary.isUnconfigured ? 'true' : undefined}>
          <${FlowSummaryParts} parts=${summary.parts} interactive=${!isReadOnly} />
          ${summary.badge ? html` <span class="flow-node-badge" data-tone=${summary.badge.tone}>${t(summary.badge.key)}</span>` : null}
        </span>
      </span>
      ${problemText
        ? html`<span class="flow-node-problem" data-severity=${hasError ? 'error' : 'warning'} title=${problemText}>
            <${Icon} name=${hasError ? 'IconAlertCircle' : 'IconAlertTriangle'} size=${16} />
            <span id=${problemId} class="visually-hidden-text">${problemText}</span>
          </span>`
        : null}
      ${isSelected && !isReadOnly
        ? html`<button type="button" class="flow-node-delete" aria-label=${t('flows.canvas.deleteNode')} title=${t('flows.canvas.deleteNode')} tabindex="-1"
            onClick=${(/** @type {MouseEvent} */ event) => {
              event.stopPropagation();
              onDelete?.(node.id);
            }}><${Icon} name="IconX" size=${14} /></button>`
        : null}
    </div>
    ${countsContent !== undefined
      ? countsContent
      : showCounts && count && flowId ? html`<${FlowNodeCounts} flowId=${flowId} nodeId=${node.id} count=${count} isFocused=${isFocused} period=${period} />` : null}
  </div>`;
}

/**
 * Pílulas embaixo do cartão: "agora" sempre (esmaecida no zero) e as outras só quando passam de zero. As pílulas do
 * passo em foco entram na ordem do Tab; as dos outros, não (uma parada de Tab no canvas).
 * @param {{ flowId: string, nodeId: string, count: FlowNodeCount, isFocused: boolean, period?: { from?: string | null, to?: string | null } }} props
 */
export function FlowNodeCounts({ flowId, nodeId, count, isFocused, period = {} }) {
  const tabIndex = isFocused ? 0 : -1;
  /** @type {Array<{ metric: string, value: number, icon: string, tone?: string, labelKey: string }>} */
  const pills = [
    { metric: 'NOW', value: count.now, icon: 'IconHourglass', labelKey: 'flows.counts.now' },
    { metric: 'PASSED', value: flowNodePassedCount(count), icon: 'IconCheck', labelKey: 'flows.counts.passed' },
    { metric: 'FAILED_NOW', value: count.failedNow, icon: 'IconAlertTriangle', tone: 'danger', labelKey: 'flows.counts.failedNow' },
    { metric: 'EXITED', value: count.exited, icon: 'IconLogout', labelKey: 'flows.counts.exited' },
  ];
  return html`<div class="flow-node-counts">
    ${pills.filter((pill) => pill.metric === 'NOW' || pill.value > 0).map((pill) => html`<${FlowCountLink} key=${pill.metric} flowId=${flowId} nodeKey=${nodeId}
      metric=${pill.metric} value=${pill.value} icon=${pill.icon} tone=${pill.tone} period=${pill.metric === 'PASSED' || pill.metric === 'EXITED' ? period : {}}
      label=${t(pill.labelKey, { count: pill.value })} tabIndex=${tabIndex} />`)}
  </div>`;
}
